// ABOUTME: Lets recruiters explicitly target a resolved WhatsApp contact episode for actions.
// ABOUTME: Confirms revision-aware reassignment without exposing candidate profile details.
import React from "react";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    MenuItem,
    TextField
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import { apiDirectCall } from "customHooks/useApi";
import { getWhatsappApiResponseData } from "functions/whatsappApiResponse";
import {
    buildWhatsappContactEpisodeActionRequest,
    createWhatsappActionRequestTracker,
    WhatsappContactEpisodeActionContext,
    WhatsappContactEpisodeMode
} from "functions/whatsappContactEpisode";

interface WhatsappContactEpisodeCandidate {
    applicantId: string;
    name: string;
    emailMasked?: string;
}

interface WhatsappResolvedContactEpisodeControlsProps {
    mode: WhatsappContactEpisodeMode;
    companyId: number;
    applicantId: string;
    contexts: WhatsappContactEpisodeActionContext[];
    selectedContext?: WhatsappContactEpisodeActionContext;
    onSelect: (context?: WhatsappContactEpisodeActionContext) => void;
    onReassigned: () => void | Promise<void>;
    notify: (message: string, variant?: any) => void;
    t: (key: string) => string;
}

const isSameContactEpisodeContext = (
    left?: WhatsappContactEpisodeActionContext,
    right?: WhatsappContactEpisodeActionContext
) =>
    left?.contactEpisodeId === right?.contactEpisodeId &&
    left?.resolutionRevision === right?.resolutionRevision;

export const WhatsappResolvedContactEpisodeControls = (
    props: WhatsappResolvedContactEpisodeControlsProps
) => {
    const {
        mode,
        companyId,
        applicantId,
        contexts,
        selectedContext,
        onSelect,
        onReassigned,
        notify,
        t
    } = props;
    const [dialogContext, setDialogContext] = React.useState<
        WhatsappContactEpisodeActionContext | undefined
    >();
    const [candidates, setCandidates] = React.useState<
        WhatsappContactEpisodeCandidate[]
    >([]);
    const [candidateId, setCandidateId] = React.useState("");
    const [loadingCandidates, setLoadingCandidates] = React.useState(false);
    const [reassigning, setReassigning] = React.useState(false);
    const candidateRequestRef = React.useRef(0);
    const actionRequestTrackerRef = React.useRef(
        createWhatsappActionRequestTracker()
    );

    React.useEffect(() => {
        if (
            dialogContext &&
            (!isSameContactEpisodeContext(selectedContext, dialogContext) ||
                !contexts.some((context) =>
                    isSameContactEpisodeContext(context, dialogContext)
                ))
        ) {
            candidateRequestRef.current += 1;
            setDialogContext(undefined);
            setCandidates([]);
            setCandidateId("");
            setLoadingCandidates(false);
            setReassigning(false);
        }
    }, [contexts, dialogContext, selectedContext]);

    if (mode !== "ACTIVE" || contexts.length === 0) {
        return null;
    }

    const closeDialog = () => {
        if (reassigning) {
            return;
        }
        candidateRequestRef.current += 1;
        actionRequestTrackerRef.current.clear("REASSIGN_EPISODE");
        setDialogContext(undefined);
        setCandidates([]);
        setCandidateId("");
        setLoadingCandidates(false);
    };

    const openReassignment = async () => {
        if (!selectedContext) {
            return;
        }

        const context = selectedContext;
        const requestId = candidateRequestRef.current + 1;
        candidateRequestRef.current = requestId;
        setDialogContext(context);
        setCandidates([]);
        setCandidateId("");
        setLoadingCandidates(true);
        try {
            const response = await apiDirectCall(
                "/whatsapp/contactepisodecandidates",
                "post",
                {
                    companyId,
                    conversationReference: {
                        scope: "CONTACT_EPISODE",
                        contactEpisodeId: context.contactEpisodeId
                    }
                }
            );
            if (candidateRequestRef.current !== requestId) {
                return;
            }
            setCandidates(
                (getWhatsappApiResponseData(response).candidates || []).filter(
                    (candidate: WhatsappContactEpisodeCandidate) =>
                        candidate.applicantId &&
                        candidate.applicantId !== applicantId
                )
            );
        } catch (error) {
            if (candidateRequestRef.current === requestId) {
                notify(t("loadContactEpisodeCandidatesError"), "error");
            }
        } finally {
            if (candidateRequestRef.current === requestId) {
                setLoadingCandidates(false);
            }
        }
    };

    const reassignEpisode = async () => {
        if (!dialogContext || !candidateId || candidateId === applicantId) {
            return;
        }

        const context = dialogContext;
        const targetApplicantId = candidateId;
        const attempt = actionRequestTrackerRef.current.prepare(
            "REASSIGN_EPISODE",
            JSON.stringify({
                companyId,
                contactEpisodeId: context.contactEpisodeId,
                targetApplicantId,
                expectedApplicantId: applicantId,
                expectedResolutionRevision: context.resolutionRevision
            })
        );
        try {
            setReassigning(true);
            await apiDirectCall(
                "/whatsapp/reassigncontactepisode",
                "post",
                buildWhatsappContactEpisodeActionRequest(
                    {
                        companyId,
                        targetApplicantId,
                        expectedApplicantId: applicantId,
                        expectedResolutionRevision: context.resolutionRevision
                    },
                    context,
                    attempt.actionRequestId
                )
            );
            actionRequestTrackerRef.current.complete(
                "REASSIGN_EPISODE",
                attempt.signature
            );
            onSelect(undefined);
            setDialogContext(undefined);
            setCandidates([]);
            setCandidateId("");
            notify(t("reassignContactEpisodeSuccess"), "success");
            await onReassigned();
        } catch (error) {
            notify(t("reassignContactEpisodeError"), "error");
        } finally {
            setReassigning(false);
        }
    };

    return (
        <Box>
            <TextField
                select
                fullWidth
                margin="dense"
                label={t("contactEpisodeActionScope")}
                value={selectedContext?.contactEpisodeId || ""}
                onChange={(event) => {
                    const context = contexts.find(
                        (item) => item.contactEpisodeId === event.target.value
                    );
                    onSelect(context);
                }}
            >
                <MenuItem value="">
                    {t("contactEpisodeActionScopeJob")}
                </MenuItem>
                {contexts.map((context) => (
                    <MenuItem
                        key={`${context.contactEpisodeId}:${context.resolutionRevision}`}
                        value={context.contactEpisodeId}
                    >
                        {`${t("contactEpisodeActionScope")} · ${
                            context.maskedPhone || context.contactEpisodeId
                        }`}
                    </MenuItem>
                ))}
            </TextField>
            {selectedContext && (
                <Box marginTop={1}>
                    <Button
                        variant="outlined"
                        size="small"
                        color="secondary"
                        onClick={openReassignment}
                    >
                        {t("reassignContactEpisode")}
                    </Button>
                </Box>
            )}

            <Dialog
                open={Boolean(dialogContext)}
                onClose={closeDialog}
                fullWidth
            >
                <DialogTitle>{t("reassignContactEpisodeTitle")}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t("reassignContactEpisodeWarning")}
                    </DialogContentText>
                    <Alert severity="warning">
                        {t("reassignContactEpisodeEffectsWarning")}
                    </Alert>
                    {loadingCandidates ? (
                        <CircularProgress size={20} />
                    ) : (
                        <TextField
                            select
                            fullWidth
                            margin="normal"
                            label={t("reassignContactEpisodeCandidate")}
                            value={candidateId}
                            onChange={(event) =>
                                setCandidateId(event.target.value)
                            }
                        >
                            <MenuItem value="">
                                {t("selectContactEpisodeCandidate")}
                            </MenuItem>
                            {candidates.map((candidate) => (
                                <MenuItem
                                    key={candidate.applicantId}
                                    value={candidate.applicantId}
                                >
                                    {`${
                                        candidate.name || candidate.applicantId
                                    }${
                                        candidate.emailMasked
                                            ? ` · ${candidate.emailMasked}`
                                            : ""
                                    }`}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDialog} disabled={reassigning}>
                        {t("cancel")}
                    </Button>
                    <Button
                        color="secondary"
                        variant="contained"
                        onClick={reassignEpisode}
                        disabled={
                            reassigning ||
                            !candidateId ||
                            candidateId === applicantId
                        }
                    >
                        {reassigning ? (
                            <CircularProgress size={18} />
                        ) : (
                            t("reassignContactEpisodeConfirm")
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default WhatsappResolvedContactEpisodeControls;
